import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import CostViewModel from '@app/models-internal/cost-view-model';
import GPSCoordinateRequest from '@app/models/gps-coordinate-request';
import ListingSeason from '@app/models/listing-season';
import ReservationEditRequest from '@app/models/reservation-edit-request';

import { Subject, throwError as observableThrowError, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  ReservationInvitation,
  ReservationModificationRequest,
  ReservationParticipant,
  ReservationParticipantViewModel,
  ReservationRequestViewModel2,
  ReservationsDetailsViewModel,
  ReservationSummary,
} from '../models';
import { DateService, UserService, LogService } from '@oa/shared-core';
import { PaymentService } from './payment.service';
import Reservation from '@app/models-internal/reservation';
import CouponList from '@app/models-internal/coupon-list';
import ReservationRequestV2 from '@app/models-internal/reservation-request-v2';
import MembershipChoice from '@app/models-internal/membership-choice.enum';
import ReservationAdminDetails from '@app/models/reservation-admin-details';
import ReservationInvitationViewModel from '@app/models/reservation-invitation-view-model';
import ReservationSummaryViewModel from '@app/models/reservation-summary-view-model';
import AddParticipantResult from '@app/models/add-participant-result';
import AcceptInvitationRequest from '@app/models/accept-invitation-request';

@Injectable()
export class ReservationService {

  private readonly _serviceName = 'ReservationService';
  private _participantCount = 1;
  private _startDate: Date;
  private _endDate: Date;

  private participantCountSource = new Subject<number>();
  private startDateSource = new Subject<Date>();
  private endDateSource = new Subject<Date>();

  public participantCountChanged = this.participantCountSource.asObservable();
  public startDateChanged = this.startDateSource.asObservable();
  public endDateChanged = this.endDateSource.asObservable();
  

  /**
   * The invite guest reservation id for the invite guest form.  If this value is set, the Invite Guest form will be shown for that reservation id
   */
  public inviteGuestReservationId: number = undefined;

  public get participantCount() {
    return this._participantCount;
  }
  public set participantCount(value: number) {
    this._participantCount = value;
    this.participantCountSource.next(value);
  }

  public get startDate() {
    return this._startDate;
  }
  public set startDate(value: Date) {
    this._startDate = value;
    this.startDateSource.next(value);
  }

  public get endDate() {
    return this._endDate;
  }
  public set endDate(value: Date) {
    this._endDate = value;
    this.endDateSource.next(value);
  }

  constructor(
    private http: HttpClient,
    private dateService: DateService,
    private paymentService: PaymentService,
    private userService: UserService,
    private log: LogService
  ) {}

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging;
  }

  public getAll() {
    return this.http.get('reservation/get/v3').pipe(
      map((res) => {
        // convert the string version of the date into a Date object
        const data = res as ReservationSummary[];
        data.forEach((d) => {
          d.startDate = this.dateService.parseDate(d.startDate);
          d.endDate = this.dateService.parseDate(d.endDate);
        });
        return data;
      })
    );
  }

  /**
   * Gets a ReservationSummary for a Booking Id
   * @param bookingId 
   * @returns 
   */
  public getReservationSummary(bookingId:string) {
    return this.http.get<ReservationSummaryViewModel>(`reservation/get/${bookingId}`).pipe(
      map((res) => {
        // convert the string version of the date into a Date object
        res.startDate = this.dateService.parseDate(res.startDate);
        res.endDate = this.dateService.parseDate(res.endDate);
        return res;
      })
    );
  }
 



  getProfileTrips() {
    return this.http.get<ReservationsDetailsViewModel>('reservations');
  }

  getLandownerReservationDetails() {
    return this.http.get<ReservationsDetailsViewModel>('reservations/GetLandownerReservations');
  }

  /**
   * Gets Reservations assocaited with a listing.
   */
  getListingReservations(listingId: number) {
    return this.http.get<Reservation[]>(`reservations/?listingId=${listingId}`);
  }

  /**
   * Gets the listing season for a reservation
   */
  public getListingSeason(reservationId: number) {
    return this.http.get<ListingSeason>(`reservation/${reservationId}/season`);
  }

  /**
   * Gets the cost of the reservation, inlcuding a breakdown of fees
   * @param reservationInfo
   */
  public getCost(reservationInfo: ReservationQuery) {
    if (this.verboseLogging) {
      console.log('ReservationService.getCost reservationInfo=', reservationInfo);
    }

    const { listingId, startDate, endDate, participantCount = 1, childParticipantCount = 0, membershipChoice } = reservationInfo;

    const coupon = reservationInfo.coupon;

    let url = `reservation/ReservationCost?startDate=${startDate}&endDate=${endDate}&listingId=${listingId}&participantCount=${participantCount}&childParticipantCount=${childParticipantCount}`;

    // only send the choice when there is one (an absent choice means "not specified", which is different from None)
    if (membershipChoice !== null && membershipChoice !== undefined) {
      url = `${url}&membershipChoice=${membershipChoice}`;
    }

    if (coupon) {
      url = `${url}&Coupon=${coupon}`;
    }

    return this.http.get<CostViewModel>(url);
  }
  

  public getInvitation(token: string) {
    return this.http.get<ReservationInvitationViewModel>(`reservation/invitation/${token}`);
  }

  /**
   * Accepts an invitation
   * @param token 
   * @param emailAddress An optional email address if the user has a different email address than the one used to invite them
   * @returns 
   */
  public acceptInvitation(acceptInvitationRequest:AcceptInvitationRequest) {
    if (!acceptInvitationRequest){
      return throwError('Missing AcceptInvitationRequest');
    }
    return this.http.post('reservation/invitation/accept', acceptInvitationRequest);
  }

  declineInvitation(token: string) {
    return this.http.post(`reservation/invitation/${token}/decline`, {});
  }

  transfer(id: number, targetUserAccountId: number) {
    return this.http.post(`reservation/${id}/transfer/${targetUserAccountId}`, {});
  }

  getModifiedCost(id: number, reservationInfo: ReservationQuery) {
    const { listingId, startDate, endDate, participantCount = 1, childParticipantCount = 0 } = reservationInfo;
    const url = `reservation/${id}/ModifiedCost?startDate=${startDate}&endDate=${endDate}&listingId=${listingId}&participantCount=${participantCount}&childParticipantCount=${childParticipantCount}`;
    return this.http.get<CostViewModel>(url);
  }

  delete({ id }: Reservation) {
    return this.http.delete('reservation/' + id);
  }

  /**
		Creates a new Reservation Request and sets the Circle Access Request for the listing.
		NOTE: this is not an actual reservation.  But this web method stores all of the reservation info
		so that the reservation process can continue after landowner grants access.
   */
  public createReservationRequest(model: ReservationRequestViewModel2) {
    return this.http.post<ReservationRequestViewModel2>('v3/reservation/request', model);
  }

  /**
   * Purchases a new booking/reservation on a listing.
   * @param reservationRequest
   */
  public post(reservationRequest: ReservationRequestV2) {
    // console.log(`${this._serviceName}.post request=`,reservationRequest);

    // if (!reservationRequest.agreementSecureAlternateId){
    //   return throwError("Missing Agreement ID on reservation request");
    // }

    if (this.paymentService.activeCoupons) {
      reservationRequest.coupons = this.paymentService.activeCoupons;
    }

    return this.http.post<Reservation>('reservation/V2', reservationRequest);
  }

  /**
   * Updates an existing reservation
   * @param id
   * @param reservationRequest
   */
  public put(id: number, reservationRequest: ReservationModificationRequest) {
    

    return this.http.put<Reservation>('reservation/' + id, reservationRequest);
  }

  /**
   * Updates an existing reservation WITHOUT affecting the price.
   * @param request
   */
  edit(request: ReservationEditRequest) {
    return this.http.put<Reservation>(`reservation/${request.reservationId}/edit`, request);
  }

  /**
   * Validates a reservation modification request using the same
   * validation that the reservation "put" web method uses.
   * @param reservationId
   * @param reservationRequest
   */
  validate(reservationId: number, reservationRequest: ReservationModificationRequest) {
    return this.http.put<ReservationModificationRequest>(`reservation\\${reservationId}\\validate`, reservationRequest);
  }

  /**
   * Updates a portion of a model (using JSON Patch Notation)
   * @param id The id of the reservation
   * @param path The path of the poperty value being modified. (e.g. "./endDate" for ReservationModel.endDate)
   * @param value The new value. Undefined or null will be treated as "remove" patch operations
   */
  patch(id: number, path: string, value) {
    const url = 'listing/' + id;
    const patch = useValue() ? { path, op: 'replace', value } : { path, op: 'remove' };

    // JSON Patch is an array of operations. Even though we have a single operation, it goes into the array
    return this.http.patch(url, [patch]).pipe(catchError(rethrowError));

    function useValue() {
      // if there is no value, we will assume remove operation
      // change the op property to remove and delete the value
      return !value && value !== 0;
    }
  }

  /**
   * Updates an existing reservation
   * @param item
   */
  update(item: Reservation) {
    return this.http.put<Reservation>('reservation', item);
  }

  // Participant Methods
  /**
   * Get Participants for a reservation
   * @param reservationId
   */
  getParticipants(reservationId: number) {
    const url = `reservation/${reservationId}/participants`;
    return this.http.get<ReservationParticipantViewModel[]>(url);
  }

  /**
   * Invites a participant to a booking/reservation
   * @param reservationId 
   * @param participant 
   * @returns 
   */
  public inviteParticipant(reservationId: number, participant: ReservationParticipant) {
    if (this.verboseLogging) {
      console.log('reservation.services.addParticipant...');
    }

    return this.http.post<AddParticipantResult>(`reservation/${reservationId}/participants/`, participant);
  }

  public deleteParticipant(reservationId: number, participantId: number) {
    if (this.verboseLogging) {
      console.log('reservation.services.deleteParticipant...');
    }
    return this.http.delete(`reservation/${reservationId}/participants/${participantId}`);
  }

  /**
   * Sends a request for the listing GPS coordinates to be sent to the user
   * api/v3/reservation/gps
   * ReservationController.TextGpsCoordinates
   * @param reservationId
   * @param userId
   */
  sendGPSCoordinates(request: GPSCoordinateRequest) {
    return this.http.post('v3/reservation/gps/', request);
  }

  /**
   * Gets the Reservation Requests for the current user
   */
  public GetUserReservationRequests() {
    const url = 'v3/reservation/request';
    return this.http.get<ReservationRequestViewModel2[]>(url);
  }

  public getReservationRequest(token: string) {
    const url = `v3/reservation/request/${token}`;
    return this.http.get<ReservationRequestViewModel2>(url);
  }

  /**
   * Get's the conversion amount for a reservation id
   */
  public getConversionAmount(reservationId: number) {
    const url = `analytics/reservation/${reservationId}/amount/conversion`;
    return this.http.get<number>(url);
  }

  /**
   * Gets Reservation Details from: api/reservations/admin/0
   * @param reservationId
   */
  getReservation(reservationId: number): Observable<ReservationAdminDetails> {
    if (!this.userService.isAdmin) {
      return EMPTY;
    }

    return this.http.get<ReservationAdminDetails>(`reservations/admin/${reservationId}`);
  }
}

export interface ReservationQuery {
  startDate: string;
  endDate: string;
  listingId: number;
  participantCount: number;
  childParticipantCount?: number;
  membershipChoice?: MembershipChoice | null;
  coupon: string;
}

function rethrowError(error) {
  return observableThrowError(error && typeof error.json === 'function' ? error.json() : error || 'Server error');
}
